
import React from 'react';
import { ShoppingCart, Layout, Sparkles, User, Search, Flower, Globe, LogOut, ClipboardList, Palette, MessageCircle, Settings, HelpCircle } from 'lucide-react';
import { Language, User as UserType } from '../types';

interface NavbarProps {
  cartCount: number;
  onCartClick: () => void;
  onChatClick: () => void;
  onAdminClick: () => void;
  onNavigate: (page: string) => void;
  onUserAuthClick: () => void;
  currentUser: UserType | null;
  onLogout: () => void;
  activePage: string;
  language: Language;
  onToggleLanguage: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ 
  cartCount, 
  onCartClick, 
  onChatClick,
  onAdminClick,
  onNavigate, 
  onUserAuthClick,
  currentUser,
  onLogout,
  activePage, 
  language, 
  onToggleLanguage 
}) => {
  const t = {
    pt: { home: 'Início', gallery: 'Galeria', custom: 'Arte Custom', faq: 'F.A.Q', elite: 'Conta', logout: 'Sair', myOrders: 'Meus Pedidos', chat: 'Chat' },
    en: { home: 'Home', gallery: 'Gallery', custom: 'Custom Art', faq: 'F.A.Q', elite: 'Account', logout: 'Logout', myOrders: 'My Orders', chat: 'Chat' }
  }[language];

  return (
    <nav className="sticky top-0 z-50 bg-black/95 backdrop-blur-lg border-b-2 border-[#8b5cf6]/50 px-4 md:px-8 py-3 md:py-4 overflow-visible shadow-[0_4px_30px_rgba(139,92,246,0.15)]">
      <div className="max-w-[1600px] mx-auto flex items-center justify-between overflow-visible">
        <div 
          className="flex items-center gap-3 cursor-pointer group whitespace-nowrap overflow-visible flex-shrink-0" 
          onClick={() => onNavigate('home')}
        >
          <div className="bg-white p-2 group-hover:bg-[#a855f7] transition-all shadow-[0_0_15px_rgba(168,85,247,0.3)] group-hover:shadow-[0_0_25px_rgba(168,85,247,0.6)] overflow-visible flex-shrink-0 flex items-center justify-center">
            <Flower className="w-4 h-4 text-black group-hover:text-white transition-transform group-hover:rotate-12" />
          </div>
          <span className="text-base md:text-lg font-black tracking-tighter text-white uppercase font-sans hidden sm:block pr-6 overflow-visible">
            NIIKO<span className="text-[#c084fc] font-extrabold tracking-widest ml-1 drop-shadow-[0_0_8px_rgba(192,132,252,0.6)]"> ARTWORK</span>
          </span>
        </div>

        <div className="hidden lg:flex items-center gap-10">
          {[
            { id: 'home', label: t.home, icon: Layout },
            { id: 'gallery', label: t.gallery, icon: Search },
            { id: 'custom-art', label: t.custom, icon: Palette },
            { id: 'faq', label: t.faq, icon: HelpCircle },
          ].map((link) => (
            <button
              key={link.id}
              onClick={() => onNavigate(link.id)}
              className={`flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.4em] transition-all hover:text-[#a855f7] ${
                activePage === link.id ? 'text-[#c084fc] drop-shadow-[0_0_8px_rgba(192,132,252,0.6)] border-b border-[#a855f7] pb-1 -mb-1' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <link.icon className="w-4 h-4" />
              {link.label}
            </button>
          ))}
          
          {currentUser && (
            <button
              onClick={() => onNavigate('my-orders')}
              className={`flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.4em] transition-all hover:text-[#a855f7] ${
                activePage === 'my-orders' ? 'text-[#c084fc] drop-shadow-[0_0_8px_rgba(192,132,252,0.6)] border-b border-[#a855f7] pb-1 -mb-1' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <ClipboardList className="w-4 h-4" />
              {t.myOrders}
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 md:gap-5">
          <div className="flex items-center bg-black/40 border border-zinc-800 p-1">
            <button 
              onClick={() => language !== 'pt' && onToggleLanguage()}
              className={`px-3 py-1.5 transition-all text-[9px] font-black ${language === 'pt' ? 'bg-[#a855f7] text-white shadow-[0_0_10px_rgba(168,85,247,0.5)]' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              BRL
            </button>
            <button 
              onClick={() => language !== 'en' && onToggleLanguage()}
              className={`px-3 py-1.5 transition-all text-[9px] font-black ${language === 'en' ? 'bg-[#a855f7] text-white shadow-[0_0_10px_rgba(168,85,247,0.5)]' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              USD
            </button>
          </div>

          <button 
            onClick={onChatClick}
            className="p-2.5 bg-black/30 border border-zinc-800 text-zinc-400 hover:text-[#a855f7] hover:border-[#a855f7]/50 hover:bg-[#a855f7]/10 transition-all"
          >
            <MessageCircle className="w-5 h-5" />
          </button>

          <button 
            onClick={onCartClick}
            className="relative p-2.5 bg-black/30 border border-zinc-800 text-zinc-400 hover:text-[#a855f7] hover:border-[#a855f7]/50 hover:bg-[#a855f7]/10 transition-all"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#a855f7] text-white text-[8px] font-black px-1.5 py-0.5 shadow-[0_0_10px_#a855f7]">
                {cartCount}
              </span>
            )}
          </button>
          
          {currentUser ? (
            <button 
              onClick={() => onNavigate('profile')}
              className={`flex items-center gap-3 px-4 py-2 border transition-all ${
                activePage === 'profile' ? 'bg-[#a855f7] border-[#a855f7] shadow-[0_0_10px_rgba(168,85,247,0.5)]' : 'bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 hover:border-[#a855f7]/50 text-zinc-300 hover:text-white'
              }`}
            >
              <div className="w-6 h-6 rounded-full overflow-hidden bg-black border border-white/10 flex-shrink-0">
                {currentUser.avatarUrl ? (
                  <img src={currentUser.avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-3 h-3 text-zinc-500" />
                  </div>
                )}
              </div>
              <span className="text-[8px] font-black text-white uppercase tracking-tight truncate max-w-[80px]">{currentUser.name}</span>
            </button>
          ) : (
            <button 
              onClick={onUserAuthClick}
              className="hidden sm:flex items-center gap-3 bg-white text-black hover:bg-[#a855f7] hover:text-white px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] transition-all"
            >
              <User className="w-4 h-4" /> {t.elite}
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
